import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField
} from '@mui/material';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { buildCsvContent, buildCsvFileName, buildCsvLine, downloadCsvContent, exportRowsToCsv } from '../../utils/csvExport';
import { formatDecimal, formatInteger, formatPercentRatio, formatPercentRatioTwoDecimals } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/fechamentoConsorcio.css';
import {
    CARTEIRA_CUSTO_OPTIONS,
    CARTEIRA_UC_OPTIONS,
    CATEGORIA_UC_OPTIONS,
    CLASSES_CUSTO,
    DISTRIBUIDORA_OPTIONS,
    UCS_INITIAL_FILTERS,
    UCS_INITIAL_SORT,
    UCS_PAGE_SIZE_OPTIONS,
    fetchFechamentoCustos,
    fetchFechamentoMeses,
    fetchFechamentoTotal,
    fetchFechamentoUcsPage,
    fetchFechamentoUgs,
    getDefaultMes
} from './fechamentoConsorcioData';

const EMPTY = '—';
const CSV_BOM = '\uFEFF';
const ZERADO_LIMITE = 1;
const PCT_MIN = 0.9;
const PCT_MAX = 1.05;
const BUSCA_DEBOUNCE_MS = 1000;

const TABS = [
    { key: 'total', label: 'Total', icon: SummarizeRoundedIcon },
    { key: 'ugs', label: 'UGs', icon: BoltRoundedIcon },
    { key: 'ucs', label: 'UCs', icon: HomeWorkRoundedIcon },
    { key: 'custos', label: 'Custos', icon: ReceiptLongRoundedIcon }
];

const tarifaFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
});

const isNil = (value) => value === null || value === undefined;

// Valor ausente (rateio ou saldo ainda não cadastrado) aparece como "—", nunca como zero.
const fmtMoney = (value) => (isNil(value) ? EMPTY : formatCurrency(value));
const fmtKwh = (value) => (isNil(value) ? EMPTY : formatInteger(value));
const fmtPct = (value) => (isNil(value) ? EMPTY : formatPercentRatio(value));
const fmtRateio = (value) => (isNil(value) ? EMPTY : formatPercentRatioTwoDecimals(value));
const fmtTarifa = (value) => (isNil(value) ? EMPTY : `${tarifaFormatter.format(value)}/kWh`);
const fmtText = (value) => (isNil(value) || value === '' ? EMPTY : String(value));

// CSV: números no formato pt-BR (abre direto no Excel em português); ausente fica vazio.
const csvMoney = (value) => (isNil(value) ? '' : formatDecimal(value));
const csvNumber = (value, digits = 2) => {
    if (isNil(value)) return '';

    return Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        useGrouping: false
    });
};

// Soma ignorando nulos; se todos forem nulos, o total também é ausente.
const sumNullable = (rows, field) => {
    const values = rows.map((row) => row[field]).filter((value) => !isNil(value));

    return values.length === 0 ? null : values.reduce((acc, value) => acc + Number(value), 0);
};

const valueClass = (value) => (!isNil(value) && value < 0 ? 'fechamento-negative' : '');

const calcTarifaLiquida = ({ receber, parcelas, pagar, kwh_compensado: kwh }) => {
    if (!kwh) return null;

    return ((receber ?? 0) + (parcelas ?? 0) - (pagar ?? 0)) / kwh;
};

const calcTarifaMedia = (valor, kwh) => (isNil(valor) || !kwh ? null : valor / kwh);

const isPctForaDaFaixa = (pct) => !isNil(pct) && (pct < PCT_MIN || pct > PCT_MAX);

const MoneyCell = ({ value, bold = false }) => (
    <TableCell align='right' className={`fechamento-cell ${valueClass(value)}${bold ? ' is-bold' : ''}`}>
        {fmtMoney(value)}
    </TableCell>
);

const NumberCell = ({ children, bold = false, className = '' }) => (
    <TableCell align='right' className={`fechamento-cell${bold ? ' is-bold' : ''} ${className}`}>
        {children}
    </TableCell>
);

const HeadCell = ({ children, align = 'left' }) => (
    <TableCell align={align} className='fechamento-head-cell'>{children}</TableCell>
);

const Flag = ({ tone = 'warning', children }) => (
    <span className={`fechamento-flag is-${tone}`}>{children}</span>
);

const LoadingState = ({ text = 'Carregando dados...' }) => (
    <div className='fechamento-state'>
        <CircularProgress size={28} />
        <span>{text}</span>
    </div>
);

const EmptyState = ({ children }) => (
    <div className='fechamento-state'>
        <span>{children}</span>
    </div>
);

const ExportButton = ({ onClick, disabled, children = 'Exportar CSV' }) => (
    <Button
        type='button'
        size='small'
        variant='outlined'
        className='fechamento-export-button'
        startIcon={<DownloadRoundedIcon />}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </Button>
);

const SelectFilter = ({ label, value, options, onChange, width = 180 }) => (
    <TextField
        select
        size='small'
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ width }}
    >
        {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
    </TextField>
);

const ResumoCard = ({ label, value, detail, highlight = false, format = fmtMoney }) => (
    <Paper variant='outlined' className={`fechamento-card${highlight ? ' is-highlight' : ''}`}>
        <span className='fechamento-card-label'>{label}</span>
        <strong className={`fechamento-card-value ${valueClass(value)}`}>{format(value)}</strong>
        {detail && <span className='fechamento-card-detail'>{detail}</span>}
    </Paper>
);

const PpaItem = ({ label, children, value }) => (
    <div className='fechamento-ppa-item'>
        <span>{label}</span>
        <strong className={valueClass(value)}>{children}</strong>
    </div>
);

/* ------------------------------------------------------------------ Total */

const EMPRESA_CSV_COLUMNS = [
    { field: 'empresa', headerName: 'Empresa' },
    { field: 'rateio', headerName: 'Rateio', csvFormatter: (value) => csvNumber(value, 6) },
    { field: 'consorcio', headerName: 'Consórcio', csvFormatter: csvMoney },
    { field: 'retencao', headerName: 'Retenção', csvFormatter: csvMoney },
    { field: 'geradoras', headerName: 'Geradoras', csvFormatter: csvMoney },
    { field: 'repasse', headerName: 'Repasse (previsto)', csvFormatter: csvMoney },
    { field: 'repasse_lancado', headerName: 'Repasse lançado', csvFormatter: csvMoney },
    { field: 'falta_lancar', headerName: 'Falta lançar', csvFormatter: csvMoney },
    { field: 'geracao', headerName: 'Geração kWh', csvFormatter: csvNumber },
    { field: 'compensacao', headerName: 'Compensação kWh', csvFormatter: csvNumber },
    { field: 'saldo_anterior', headerName: 'Saldo anterior kWh', csvFormatter: csvNumber },
    { field: 'saldo_final', headerName: 'Saldo final kWh', csvFormatter: csvNumber }
];

const EMPRESA_SUM_FIELDS = EMPRESA_CSV_COLUMNS.slice(1).map((column) => column.field);

const withFaltaLancar = (empresa) => ({
    ...empresa,
    falta_lancar: isNil(empresa.repasse) ? null : empresa.repasse - (empresa.repasse_lancado ?? 0)
});

const buildEmpresaTotals = (empresas) => EMPRESA_SUM_FIELDS.reduce((acc, field) => ({
    ...acc,
    [field]: sumNullable(empresas, field)
}), { empresa: 'Total' });

const buildResumoLines = (total) => {
    const { consolidado = {}, ppa = {} } = total;

    return [
        ['A receber', consolidado.a_receber],
        ['A pagar', consolidado.a_pagar],
        ['Ajuste de fechamento', consolidado.ajuste_fechamento],
        ['Despesas operacionais', consolidado.despesas_operacionais],
        ['Consolidado', consolidado.total],
        ['Geradoras', consolidado.geradoras],
        ['Retenção (previsto)', consolidado.retencao],
        ['Retenção (lançado)', consolidado.retencao_lancada],
        ['Repasse às SPEs (previsto)', consolidado.repasse],
        ['Repasse às SPEs (lançado)', consolidado.repasse_lancado],
        ['PPA - Receita UCs', ppa.receita_ucs],
        ['PPA - Custo UCs', ppa.custo_ucs],
        ['PPA - AGF geradores', ppa.agf_gerador],
        ['PPA - Faturas UG', ppa.faturas_ug],
        ['PPA - Pagar geradores (previsto)', ppa.pagar_gerador],
        ['PPA - Pagar geradores (lançado)', ppa.pagar_gerador_lancado],
        ['PPA - Outros', ppa.outros],
        ['PPA - SETTA', ppa.setta],
        ['PPA - SETTA após diferido', ppa.setta_apos_diferido]
    ];
};

const buildEnergiaPpaLines = ({ ppa = {} }) => [
    ['PPA - Injetado (excedente) contabilizado kWh', ppa.geracao],
    ['PPA - Compensado nas UCs kWh', ppa.kwh_compensado],
    ['PPA - Saldo anterior kWh', ppa.saldo_anterior],
    ['PPA - Saldo final kWh', ppa.saldo_final]
];

const buildTarifaLines = ({ ppa = {} }, totais) => [
    ['Tarifa média SPEs R$/kWh', calcTarifaMedia(totais.consorcio, totais.compensacao)],
    ['PPA - Tarifa líquida média R$/kWh', ppa.tarifa_liquida_media],
    ['PPA - Tarifa média geradores R$/kWh', calcTarifaMedia(ppa.agf_gerador, ppa.geracao)]
];

const exportTotalCsv = (total, empresas, mes) => {
    const totais = buildEmpresaTotals(empresas);
    const lines = [
        buildCsvLine(['Indicador', 'Valor']),
        ...buildResumoLines(total).map(([label, value]) => buildCsvLine([label, csvMoney(value)])),
        ...buildTarifaLines(total, totais).map(([label, value]) => buildCsvLine([label, csvNumber(value, 4)])),
        ...buildEnergiaPpaLines(total).map(([label, value]) => buildCsvLine([label, csvNumber(value)])),
        ''
    ];
    // A segunda tabela vai no mesmo arquivo; só o primeiro bloco leva o BOM.
    const empresasCsv = buildCsvContent(EMPRESA_CSV_COLUMNS, [...empresas, totais]).replace(CSV_BOM, '');

    downloadCsvContent(buildCsvFileName(`fechamento-total-${mes}`), `${CSV_BOM}${lines.join('\r\n')}\r\n${empresasCsv}`);
};

const TotalTab = ({ total, mes }) => {
    const empresas = useMemo(() => (total?.empresas ?? []).map(withFaltaLancar), [total]);
    const totais = useMemo(() => buildEmpresaTotals(empresas), [empresas]);

    if (!total) return <EmptyState>Total do fechamento indisponível para este mês.</EmptyState>;

    const { consolidado = {}, ppa = {}, alertas = [] } = total;

    return (
        <div className='fechamento-tab-body'>
            <div className='fechamento-toolbar'>
                <h2 className='fechamento-section-title'>Consolidado do projeto principal</h2>
                <ExportButton onClick={() => exportTotalCsv(total, empresas, mes)} />
            </div>

            {alertas.length > 0 && (
                <Alert severity='warning' className='fechamento-alertas'>
                    <strong>{alertas.length === 1 ? '1 alerta' : `${alertas.length} alertas`}</strong>
                    <ul>
                        {alertas.map((alerta, index) => <li key={index}>{alerta}</li>)}
                    </ul>
                </Alert>
            )}

            <div className='fechamento-cards'>
                <ResumoCard label='A receber' value={consolidado.a_receber} />
                <ResumoCard label='A pagar' value={consolidado.a_pagar} />
                <ResumoCard label='Ajuste de fechamento' value={consolidado.ajuste_fechamento} />
                <ResumoCard label='Despesas operacionais' value={consolidado.despesas_operacionais} />
                <ResumoCard label='Consolidado' value={consolidado.total} highlight />
                <ResumoCard label='Retenção (previsto)' value={consolidado.retencao} detail={`Lançado: ${fmtMoney(consolidado.retencao_lancada)}`} />
                <ResumoCard label='Repasse às SPEs (previsto)' value={consolidado.repasse} detail={`Lançado: ${fmtMoney(consolidado.repasse_lancado)}`} />
                <ResumoCard
                    label='Tarifa média SPEs'
                    value={calcTarifaMedia(totais.consorcio, totais.compensacao)}
                    format={fmtTarifa}
                    detail='Consórcio ÷ compensação kWh'
                />
                <ResumoCard label='Resultado PPA' value={ppa.setta_apos_diferido} detail='SETTA após diferido' highlight />
            </div>

            <h3 className='fechamento-subtitle'>Por empresa</h3>
            <TableContainer component={Paper} variant='outlined' className='fechamento-table-shell'>
                <Table size='small' stickyHeader className='fechamento-table'>
                    <TableHead>
                        <TableRow>
                            <HeadCell>Empresa</HeadCell>
                            <HeadCell align='right'>Rateio %</HeadCell>
                            <HeadCell align='right'>Consórcio</HeadCell>
                            <HeadCell align='right'>Retenção</HeadCell>
                            <HeadCell align='right'>Geradoras</HeadCell>
                            <HeadCell align='right'>Repasse (previsto)</HeadCell>
                            <HeadCell align='right'>Repasse lançado</HeadCell>
                            <HeadCell align='right'>Falta lançar</HeadCell>
                            <HeadCell align='right'>Geração kWh</HeadCell>
                            <HeadCell align='right'>Compensação kWh</HeadCell>
                            <HeadCell align='right'>Saldo anterior</HeadCell>
                            <HeadCell align='right'>Saldo final</HeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {empresas.map((empresa) => (
                            <TableRow key={empresa.empresa_id ?? empresa.empresa} hover>
                                <TableCell className='fechamento-cell is-bold'>{empresa.empresa}</TableCell>
                                <NumberCell>{fmtRateio(empresa.rateio)}</NumberCell>
                                <MoneyCell value={empresa.consorcio} />
                                <MoneyCell value={empresa.retencao} />
                                <MoneyCell value={empresa.geradoras} />
                                <MoneyCell value={empresa.repasse} />
                                <MoneyCell value={empresa.repasse_lancado} />
                                <MoneyCell value={empresa.falta_lancar} />
                                <NumberCell>{fmtKwh(empresa.geracao)}</NumberCell>
                                <NumberCell>{fmtKwh(empresa.compensacao)}</NumberCell>
                                <NumberCell className={valueClass(empresa.saldo_anterior)}>{fmtKwh(empresa.saldo_anterior)}</NumberCell>
                                <NumberCell className={valueClass(empresa.saldo_final)}>{fmtKwh(empresa.saldo_final)}</NumberCell>
                            </TableRow>
                        ))}
                        {empresas.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={12} align='center' className='fechamento-feedback-cell'>
                                    Nenhuma empresa no rateio deste mês.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {empresas.length > 0 && (
                        <TableFooter>
                            <TableRow className='fechamento-total-row'>
                                <TableCell className='fechamento-cell is-bold'>Total</TableCell>
                                <NumberCell bold>{fmtRateio(totais.rateio)}</NumberCell>
                                <MoneyCell value={totais.consorcio} bold />
                                <MoneyCell value={totais.retencao} bold />
                                <MoneyCell value={totais.geradoras} bold />
                                <MoneyCell value={totais.repasse} bold />
                                <MoneyCell value={totais.repasse_lancado} bold />
                                <MoneyCell value={totais.falta_lancar} bold />
                                <NumberCell bold>{fmtKwh(totais.geracao)}</NumberCell>
                                <NumberCell bold>{fmtKwh(totais.compensacao)}</NumberCell>
                                <NumberCell bold>{fmtKwh(totais.saldo_anterior)}</NumberCell>
                                <NumberCell bold>{fmtKwh(totais.saldo_final)}</NumberCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>

            <h3 className='fechamento-subtitle'>PPA</h3>
            <Paper variant='outlined' className='fechamento-ppa'>
                <PpaItem label='Receita UCs' value={ppa.receita_ucs}>{fmtMoney(ppa.receita_ucs)}</PpaItem>
                <PpaItem label='Custo UCs' value={ppa.custo_ucs}>{fmtMoney(ppa.custo_ucs)}</PpaItem>
                <PpaItem label='Tarifa líquida média'>{fmtTarifa(ppa.tarifa_liquida_media)}</PpaItem>
                <PpaItem label='Tarifa média geradores'>{fmtTarifa(calcTarifaMedia(ppa.agf_gerador, ppa.geracao))}</PpaItem>
                <PpaItem label='AGF geradores' value={ppa.agf_gerador}>{fmtMoney(ppa.agf_gerador)}</PpaItem>
                <PpaItem label='Faturas UG' value={ppa.faturas_ug}>{fmtMoney(ppa.faturas_ug)}</PpaItem>
                <PpaItem label='Pagar geradores (previsto)' value={ppa.pagar_gerador}>{fmtMoney(ppa.pagar_gerador)}</PpaItem>
                <PpaItem label='Pagar geradores (lançado)' value={ppa.pagar_gerador_lancado}>{fmtMoney(ppa.pagar_gerador_lancado)}</PpaItem>
                <PpaItem label='Outros' value={ppa.outros}>{fmtMoney(ppa.outros)}</PpaItem>
                <PpaItem label='SETTA' value={ppa.setta}>{fmtMoney(ppa.setta)}</PpaItem>
                <PpaItem label='SETTA após diferido' value={ppa.setta_apos_diferido}>{fmtMoney(ppa.setta_apos_diferido)}</PpaItem>
            </Paper>

            <h3 className='fechamento-subtitle'>PPA - Energia</h3>
            <Paper variant='outlined' className='fechamento-ppa'>
                <PpaItem label='Injetado (excedente) contabilizado kWh'>{fmtKwh(ppa.geracao)}</PpaItem>
                <PpaItem label='Compensado nas UCs kWh'>{fmtKwh(ppa.kwh_compensado)}</PpaItem>
                <PpaItem label='Saldo anterior kWh' value={ppa.saldo_anterior}>{fmtKwh(ppa.saldo_anterior)}</PpaItem>
                <PpaItem label='Saldo final kWh' value={ppa.saldo_final}>{fmtKwh(ppa.saldo_final)}</PpaItem>
            </Paper>
        </div>
    );
};

/* -------------------------------------------------------------------- UGs */

const CARTEIRA_LABELS = { PRINCIPAL: 'Principal', PPA: 'PPA', SALDO: 'UC Saldo' };

const UG_CSV_COLUMNS = [
    { field: 'carteira', headerName: 'Carteira' },
    { field: 'empresa', headerName: 'Empresa' },
    { field: 'cc', headerName: 'CC' },
    { field: 'unidade', headerName: 'Unidade' },
    { field: 'periodo', headerName: 'Período' },
    { field: 'notafiscal', headerName: 'NF' },
    { field: 'valor_fatura', headerName: 'Valor fatura', csvFormatter: csvMoney },
    { field: 'excedente', headerName: 'Excedente kWh', csvFormatter: csvNumber },
    { field: 'sem_excedente', headerName: 'Sem excedente', csvFormatter: (value) => (value ? 'Sim' : 'Não') },
    { field: 'gerado_inversor', headerName: 'Gerado inversor kWh', csvFormatter: csvNumber },
    { field: 'perda_2_5', headerName: 'Perda 2,5% kWh', csvFormatter: csvNumber },
    { field: 'pct', headerName: '%', csvFormatter: (value) => csvNumber(value, 4) },
    { field: 'tarifa_ppa', headerName: 'Tarifa PPA', csvFormatter: (value) => csvNumber(value, 6) },
    { field: 'agf_gerador', headerName: 'AGF', csvFormatter: csvMoney },
    { field: 'pagar_gerador', headerName: 'Pagar gerador', csvFormatter: csvMoney },
    { field: 'diferida', headerName: 'Diferida', csvFormatter: (value) => (value ? 'Sim' : 'Não') }
];

const UG_SUM_FIELDS = ['valor_fatura', 'excedente', 'gerado_inversor', 'perda_2_5', 'agf_gerador', 'pagar_gerador'];

const sumUgs = (rows) => UG_SUM_FIELDS.reduce((acc, field) => ({ ...acc, [field]: sumNullable(rows, field) }), {});

const compareText = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR');

const UgRow = ({ ug }) => {
    const pctForaDaFaixa = isPctForaDaFaixa(ug.pct);
    const isAlerta = ug.sem_excedente || pctForaDaFaixa;

    return (
        <TableRow hover className={isAlerta ? 'fechamento-row-warning' : ''}>
            <TableCell className='fechamento-cell'>
                <span className={`fechamento-carteira is-${String(ug.carteira).toLowerCase()}`}>{CARTEIRA_LABELS[ug.carteira] ?? ug.carteira}</span>
            </TableCell>
            <TableCell className='fechamento-cell'>{fmtText(ug.empresa)}</TableCell>
            <TableCell className='fechamento-cell'>{fmtText(ug.cc)}</TableCell>
            <TableCell className='fechamento-cell'>
                <div className='fechamento-unidade'>
                    <span>{fmtText(ug.unidade)}</span>
                    {ug.sem_excedente && <Flag>sem excedente</Flag>}
                </div>
            </TableCell>
            <TableCell className='fechamento-cell'>{fmtText(ug.periodo)}</TableCell>
            <TableCell className='fechamento-cell'>{fmtText(ug.notafiscal)}</TableCell>
            <MoneyCell value={ug.valor_fatura} />
            <NumberCell>{fmtKwh(ug.excedente)}</NumberCell>
            <NumberCell>{fmtKwh(ug.gerado_inversor)}</NumberCell>
            <NumberCell>{fmtKwh(ug.perda_2_5)}</NumberCell>
            <NumberCell className={pctForaDaFaixa ? 'fechamento-pct-out' : ''}>{fmtPct(ug.pct)}</NumberCell>
            <NumberCell>{fmtTarifa(ug.tarifa_ppa)}</NumberCell>
            <MoneyCell value={ug.agf_gerador} />
            <MoneyCell value={ug.pagar_gerador} />
            <TableCell className='fechamento-cell'>{ug.diferida ? <Flag tone='info'>diferida</Flag> : ''}</TableCell>
        </TableRow>
    );
};

const UgTotalsCells = ({ totais }) => (
    <>
        <MoneyCell value={totais.valor_fatura} bold />
        <NumberCell bold>{fmtKwh(totais.excedente)}</NumberCell>
        <NumberCell bold>{fmtKwh(totais.gerado_inversor)}</NumberCell>
        <NumberCell bold>{fmtKwh(totais.perda_2_5)}</NumberCell>
        <TableCell className='fechamento-cell' />
        <TableCell className='fechamento-cell' />
        <MoneyCell value={totais.agf_gerador} bold />
        <MoneyCell value={totais.pagar_gerador} bold />
        <TableCell className='fechamento-cell' />
    </>
);

const UgsTab = ({ ugs, mes }) => {
    const [agrupar, setAgrupar] = useState(false);

    const filtradas = useMemo(() => (
        agrupar
            ? [...ugs].sort((a, b) => compareText(a.empresa, b.empresa) || compareText(a.unidade, b.unidade))
            : ugs
    ), [agrupar, ugs]);

    const grupos = useMemo(() => {
        if (!agrupar) return [];

        const porEmpresa = new Map();
        filtradas.forEach((ug) => {
            const chave = ug.empresa ?? 'Sem empresa';
            if (!porEmpresa.has(chave)) porEmpresa.set(chave, []);
            porEmpresa.get(chave).push(ug);
        });

        return [...porEmpresa.entries()].map(([empresa, rows]) => ({ empresa, rows, totais: sumUgs(rows) }));
    }, [agrupar, filtradas]);

    const totais = useMemo(() => sumUgs(filtradas), [filtradas]);
    const qtdAlertas = useMemo(() => filtradas.filter((ug) => ug.sem_excedente || isPctForaDaFaixa(ug.pct)).length, [filtradas]);

    return (
        <div className='fechamento-tab-body'>
            <div className='fechamento-toolbar'>
                <div className='fechamento-filters'>
                    <FormControlLabel
                        control={<Checkbox size='small' checked={agrupar} onChange={(event) => setAgrupar(event.target.checked)} />}
                        label='Agrupar por empresa'
                    />
                </div>
                <div className='fechamento-toolbar-actions'>
                    <span className='fechamento-count'>
                        {filtradas.length} UGs{qtdAlertas > 0 ? ` · ${qtdAlertas} com alerta` : ''}
                    </span>
                    <ExportButton
                        disabled={filtradas.length === 0}
                        onClick={() => exportRowsToCsv({ baseName: `fechamento-ugs-${mes}`, columns: UG_CSV_COLUMNS, rows: filtradas })}
                    />
                </div>
            </div>

            <TableContainer component={Paper} variant='outlined' className='fechamento-table-shell is-tall'>
                <Table size='small' stickyHeader className='fechamento-table'>
                    <TableHead>
                        <TableRow>
                            <HeadCell>Carteira</HeadCell>
                            <HeadCell>Empresa</HeadCell>
                            <HeadCell>CC</HeadCell>
                            <HeadCell>Unidade</HeadCell>
                            <HeadCell>Período</HeadCell>
                            <HeadCell>NF</HeadCell>
                            <HeadCell align='right'>Valor fatura</HeadCell>
                            <HeadCell align='right'>Excedente kWh</HeadCell>
                            <HeadCell align='right'>Gerado inversor</HeadCell>
                            <HeadCell align='right'>Perda 2,5%</HeadCell>
                            <HeadCell align='right'>%</HeadCell>
                            <HeadCell align='right'>Tarifa PPA</HeadCell>
                            <HeadCell align='right'>AGF</HeadCell>
                            <HeadCell align='right'>Pagar gerador</HeadCell>
                            <HeadCell>Diferida</HeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agrupar
                            ? grupos.map((grupo) => (
                                <React.Fragment key={grupo.empresa}>
                                    <TableRow className='fechamento-group-row'>
                                        <TableCell colSpan={6} className='fechamento-cell is-bold'>
                                            {grupo.empresa} <span className='fechamento-muted'>· {grupo.rows.length} UGs</span>
                                        </TableCell>
                                        <UgTotalsCells totais={grupo.totais} />
                                    </TableRow>
                                    {grupo.rows.map((ug, index) => <UgRow key={`${ug.cc}-${ug.notafiscal}-${index}`} ug={ug} />)}
                                </React.Fragment>
                            ))
                            : filtradas.map((ug, index) => <UgRow key={`${ug.cc}-${ug.notafiscal}-${index}`} ug={ug} />)}
                        {filtradas.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={15} align='center' className='fechamento-feedback-cell'>
                                    Nenhuma UG neste mês.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {filtradas.length > 0 && (
                        <TableFooter>
                            <TableRow className='fechamento-total-row'>
                                <TableCell colSpan={6} className='fechamento-cell is-bold'>Total ({filtradas.length} UGs)</TableCell>
                                <UgTotalsCells totais={totais} />
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
        </div>
    );
};

/* -------------------------------------------------------------------- UCs */

const UC_COLUMNS = [
    { field: 'carteira', headerName: 'Carteira' },
    { field: 'cc', headerName: 'CC', sortable: true },
    { field: 'unidade', headerName: 'Unidade', sortable: true },
    { field: 'categoria', headerName: 'Cat.', sortable: true },
    { field: 'distribuidora', headerName: 'Distribuidora', sortable: true },
    { field: 'periodo', headerName: 'Período', sortable: true },
    { field: 'notafiscal', headerName: 'NF', sortable: true },
    { field: 'pagar', headerName: 'A pagar', sortable: true, numeric: true },
    { field: 'receber', headerName: 'A receber', sortable: true, numeric: true },
    { field: 'cobranca_calculada', headerName: 'Cobrança calculada', sortable: true, numeric: true },
    { field: 'parcelas', headerName: 'Parcelas', sortable: true, numeric: true },
    { field: 'kwh_compensado', headerName: 'kWh compensado', sortable: true, numeric: true },
    { field: 'tarifa_liquida', headerName: 'Tarifa líquida', sortable: true, numeric: true },
    { field: 'flags', headerName: 'Alertas' }
];

const UC_CSV_COLUMNS = [
    { field: 'carteira', headerName: 'Carteira' },
    { field: 'cc', headerName: 'CC' },
    { field: 'unidade', headerName: 'Unidade' },
    { field: 'categoria', headerName: 'Categoria' },
    { field: 'distribuidora', headerName: 'Distribuidora' },
    { field: 'periodo', headerName: 'Período' },
    { field: 'notafiscal', headerName: 'NF' },
    { field: 'pagar', headerName: 'A pagar', csvFormatter: csvMoney },
    { field: 'receber', headerName: 'A receber', csvFormatter: csvMoney },
    { field: 'cobranca_calculada', headerName: 'Cobrança calculada', csvFormatter: csvMoney },
    { field: 'parcelas', headerName: 'Parcelas', csvFormatter: csvMoney },
    { field: 'kwh_compensado', headerName: 'kWh compensado', csvFormatter: csvNumber },
    { field: 'tarifa_liquida', headerName: 'Tarifa líquida R$/kWh', csvFormatter: (value, row) => csvNumber(calcTarifaLiquida(row), 4) },
    { field: 'desconto_indicacao', headerName: 'Desconto indicação', csvFormatter: csvMoney },
    { field: 'multa', headerName: 'Multa', csvFormatter: csvMoney },
    { field: 'desconto_contratual', headerName: 'Desconto contratual', csvFormatter: (value) => csvNumber(value, 4) },
    { field: 'divergente', headerName: 'Divergente', csvFormatter: (value) => (value ? 'Sim' : 'Não') },
    { field: 'abaixo_minimo', headerName: 'Abaixo de R$ 10', csvFormatter: (value) => (value ? 'Sim' : 'Não') },
    { field: 'fatura_apagada', headerName: 'Fatura apagada', csvFormatter: (value) => (value ? 'Sim' : 'Não') }
];

const UcsTab = ({ mes, isActive, onError }) => {
    const [filters, setFilters] = useState(UCS_INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(UCS_INITIAL_FILTERS);
    const [sort, setSort] = useState(UCS_INITIAL_SORT);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(UCS_PAGE_SIZE_OPTIONS[1]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totais, setTotais] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const lastRequestKey = useRef('');
    const previousMes = useRef(mes);

    // Troca de mês volta para a primeira página.
    useEffect(() => {
        if (previousMes.current !== mes) {
            previousMes.current = mes;
            setPage(1);
        }
    }, [mes]);

    // Só a busca textual espera 1 s; os selects aplicam na hora.
    useEffect(() => {
        if (filters.busca === appliedFilters.busca) return undefined;

        const timeoutId = window.setTimeout(() => {
            setAppliedFilters((prev) => ({ ...prev, busca: filters.busca }));
            setPage(1);
        }, BUSCA_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [appliedFilters.busca, filters.busca]);

    useEffect(() => {
        if (!isActive || !mes) return undefined;

        const requestKey = JSON.stringify({ mes, appliedFilters, sort, page, pageSize });
        // Voltar para a aba sem mudar nada não refaz a consulta.
        if (requestKey === lastRequestKey.current) return undefined;

        let ignore = false;
        setIsFetching(true);

        fetchFechamentoUcsPage({ mes, filters: appliedFilters, sort, page, pageSize })
            .then((data) => {
                if (ignore) return;
                lastRequestKey.current = requestKey;
                setRows(data.items);
                setTotal(data.total);
                setTotais(data.totais);
                setHasLoaded(true);
            })
            .catch((error) => {
                if (ignore) return;
                // Sem a primeira página, mostra a tabela vazia com os filtros para tentar de novo.
                setHasLoaded(true);
                onError(error.message);
            })
            .finally(() => {
                if (!ignore) setIsFetching(false);
            });

        return () => { ignore = true; };
    }, [appliedFilters, isActive, mes, onError, page, pageSize, sort]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));

        if (field !== 'busca') {
            setAppliedFilters((prev) => ({ ...prev, [field]: value }));
            setPage(1);
        }
    };

    const handleSort = (field) => {
        setSort((prev) => ({
            ordenar: field,
            direcao: prev.ordenar === field && prev.direcao === 'asc' ? 'desc' : 'asc'
        }));
        setPage(1);
    };

    const handleClear = () => {
        setFilters(UCS_INITIAL_FILTERS);
        setAppliedFilters(UCS_INITIAL_FILTERS);
        setSort(UCS_INITIAL_SORT);
        setPage(1);
    };

    if (!hasLoaded) return <LoadingState text='Carregando UCs...' />;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className='fechamento-tab-body'>
            <div className='fechamento-toolbar'>
                <div className='fechamento-filters'>
                    <SelectFilter label='Carteira' value={filters.carteira} options={CARTEIRA_UC_OPTIONS} onChange={(value) => handleFilterChange('carteira', value)} width={150} />
                    <TextField
                        size='small'
                        label='Buscar CC ou unidade'
                        value={filters.busca}
                        onChange={(event) => handleFilterChange('busca', event.target.value)}
                        sx={{ width: 240 }}
                    />
                    <SelectFilter
                        label='Distribuidora'
                        value={filters.distribuidora}
                        options={[{ value: '', label: 'Todas' }, ...DISTRIBUIDORA_OPTIONS.map((item) => ({ value: item, label: item }))]}
                        onChange={(value) => handleFilterChange('distribuidora', value)}
                    />
                    <SelectFilter
                        label='Categoria'
                        value={filters.categoria}
                        options={[{ value: '', label: 'Todas' }, ...CATEGORIA_UC_OPTIONS.map((item) => ({ value: item, label: item }))]}
                        onChange={(value) => handleFilterChange('categoria', value)}
                        width={130}
                    />
                    <FormControlLabel
                        control={<Checkbox size='small' checked={filters.so_divergentes} onChange={(event) => handleFilterChange('so_divergentes', event.target.checked)} />}
                        label='Só divergentes'
                    />
                    <Button type='button' size='small' onClick={handleClear}>Limpar filtros</Button>
                </div>
                <div className='fechamento-toolbar-actions'>
                    {isFetching && <CircularProgress size={18} />}
                    <span className='fechamento-count'>{formatInteger(total)} UCs</span>
                    <ExportButton
                        disabled={rows.length === 0}
                        onClick={() => exportRowsToCsv({ baseName: `fechamento-ucs-${mes}-pagina-${page}`, columns: UC_CSV_COLUMNS, rows })}
                    >
                        Exportar página
                    </ExportButton>
                </div>
            </div>

            <TableContainer component={Paper} variant='outlined' className={`fechamento-table-shell is-tall${isFetching ? ' is-fetching' : ''}`}>
                <Table size='small' stickyHeader className='fechamento-table'>
                    <TableHead>
                        <TableRow>
                            {UC_COLUMNS.map((column) => (
                                <TableCell key={column.field} align={column.numeric ? 'right' : 'left'} className='fechamento-head-cell'>
                                    {column.sortable
                                        ? (
                                            <TableSortLabel
                                                active={sort.ordenar === column.field}
                                                direction={sort.ordenar === column.field ? sort.direcao : 'asc'}
                                                onClick={() => handleSort(column.field)}
                                            >
                                                {column.headerName}
                                            </TableSortLabel>
                                        )
                                        : column.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((uc, index) => {
                            const isAlerta = uc.divergente || uc.fatura_apagada;

                            return (
                                <TableRow key={`${uc.cc}-${uc.notafiscal}-${index}`} hover className={isAlerta ? 'fechamento-row-warning' : ''}>
                                    <TableCell className='fechamento-cell'>
                                        <span className={`fechamento-carteira is-${String(uc.carteira).toLowerCase()}`}>{CARTEIRA_LABELS[uc.carteira] ?? uc.carteira}</span>
                                    </TableCell>
                                    <TableCell className='fechamento-cell'>{fmtText(uc.cc)}</TableCell>
                                    <TableCell className='fechamento-cell fechamento-unidade-cell'>{fmtText(uc.unidade)}</TableCell>
                                    <TableCell className='fechamento-cell'>{fmtText(uc.categoria)}</TableCell>
                                    <TableCell className='fechamento-cell'>{fmtText(uc.distribuidora)}</TableCell>
                                    <TableCell className='fechamento-cell'>{fmtText(uc.periodo)}</TableCell>
                                    <TableCell className='fechamento-cell'>{fmtText(uc.notafiscal)}</TableCell>
                                    <MoneyCell value={uc.pagar} />
                                    <MoneyCell value={uc.receber} />
                                    <MoneyCell value={uc.cobranca_calculada} />
                                    <MoneyCell value={uc.parcelas} />
                                    <NumberCell>{fmtKwh(uc.kwh_compensado)}</NumberCell>
                                    <NumberCell>{fmtTarifa(calcTarifaLiquida(uc))}</NumberCell>
                                    <TableCell className='fechamento-cell'>
                                        <div className='fechamento-flags'>
                                            {uc.divergente && <Flag tone='error'>divergente</Flag>}
                                            {uc.abaixo_minimo && <Flag tone='info'>{'< R$10'}</Flag>}
                                            {uc.fatura_apagada && <Flag tone='error'>fatura apagada</Flag>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={UC_COLUMNS.length} align='center' className='fechamento-feedback-cell'>
                                    Nenhuma UC para os filtros aplicados.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {totais && (
                        <TableFooter>
                            {/* Totais de todo o filtro, vindos do backend (não é a soma da página). */}
                            <TableRow className='fechamento-total-row'>
                                <TableCell colSpan={7} className='fechamento-cell is-bold'>
                                    Total do filtro ({formatInteger(total)} UCs)
                                </TableCell>
                                <MoneyCell value={totais.pagar} bold />
                                <MoneyCell value={totais.receber} bold />
                                <MoneyCell value={totais.cobranca_calculada} bold />
                                <MoneyCell value={totais.parcelas} bold />
                                <NumberCell bold>{fmtKwh(totais.kwh_compensado)}</NumberCell>
                                <NumberCell bold>{fmtTarifa(calcTarifaLiquida(totais))}</NumberCell>
                                <TableCell className='fechamento-cell' />
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>

            <div className='fechamento-pagination'>
                <span className='fechamento-count'>Página {page} de {totalPages}</span>
                <TablePagination
                    component='div'
                    count={total}
                    page={Math.min(page - 1, totalPages - 1)}
                    onPageChange={(event, newPage) => setPage(newPage + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(event) => {
                        setPageSize(parseInt(event.target.value, 10));
                        setPage(1);
                    }}
                    rowsPerPageOptions={UCS_PAGE_SIZE_OPTIONS}
                    labelRowsPerPage='UCs por página'
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
            </div>
        </div>
    );
};

/* ----------------------------------------------------------------- Custos */

const CLASSE_LABELS = CLASSES_CUSTO.reduce((acc, classe) => ({ ...acc, [classe.value]: classe.label }), {});

const CUSTO_CSV_COLUMNS = [
    { field: 'carteira', headerName: 'Carteira' },
    { field: 'classe', headerName: 'Classe', csvFormatter: (value) => CLASSE_LABELS[value] ?? value },
    { field: 'tipo', headerName: 'Tipo' },
    { field: 'codigo_lancamento', headerName: 'Código lançamento' },
    { field: 'categoria', headerName: 'Categoria' },
    { field: 'nome_categoria', headerName: 'Nome categoria' },
    { field: 'fornecedor', headerName: 'Fornecedor' },
    { field: 'descricao', headerName: 'Descrição' },
    { field: 'valor', headerName: 'Valor', csvFormatter: csvMoney }
];

const groupCustos = (custos) => {
    const ordemClasses = [
        ...CLASSES_CUSTO.map((classe) => classe.value),
        ...[...new Set(custos.map((custo) => custo.classe))].filter((classe) => !CLASSE_LABELS[classe])
    ];

    return ordemClasses
        .map((classe) => {
            const rowsClasse = custos.filter((custo) => custo.classe === classe);
            const categorias = [...new Set(rowsClasse.map((custo) => custo.categoria))]
                .sort(compareText)
                .map((categoria) => {
                    const rows = rowsClasse.filter((custo) => custo.categoria === categoria);

                    return { categoria, nome: rows[0]?.nome_categoria, rows, subtotal: sumNullable(rows, 'valor') };
                });

            return { classe, label: CLASSE_LABELS[classe] ?? fmtText(classe), categorias, rows: rowsClasse, subtotal: sumNullable(rowsClasse, 'valor') };
        })
        .filter((grupo) => grupo.rows.length > 0);
};

const CustosTab = ({ custos, mes }) => {
    const [carteira, setCarteira] = useState('TODAS');
    const filtrados = useMemo(() => (carteira === 'TODAS' ? custos : custos.filter((custo) => custo.carteira === carteira)), [carteira, custos]);
    const grupos = useMemo(() => groupCustos(filtrados), [filtrados]);
    const totalGeral = useMemo(() => sumNullable(filtrados, 'valor'), [filtrados]);
    const exportRows = useMemo(() => grupos.flatMap((grupo) => grupo.categorias.flatMap((categoria) => categoria.rows)), [grupos]);

    return (
        <div className='fechamento-tab-body'>
            <div className='fechamento-toolbar'>
                <div className='fechamento-filters'>
                    <SelectFilter label='Carteira' value={carteira} options={CARTEIRA_CUSTO_OPTIONS} onChange={setCarteira} />
                </div>
                <div className='fechamento-toolbar-actions'>
                    <span className='fechamento-count'>{filtrados.length} lançamentos</span>
                    <ExportButton
                        disabled={exportRows.length === 0}
                        onClick={() => exportRowsToCsv({ baseName: `fechamento-custos-${mes}`, columns: CUSTO_CSV_COLUMNS, rows: exportRows })}
                    />
                </div>
            </div>

            <TableContainer component={Paper} variant='outlined' className='fechamento-table-shell is-tall'>
                <Table size='small' stickyHeader className='fechamento-table'>
                    <TableHead>
                        <TableRow>
                            <HeadCell>Tipo</HeadCell>
                            <HeadCell>Código lançamento</HeadCell>
                            <HeadCell>Categoria</HeadCell>
                            <HeadCell>Fornecedor</HeadCell>
                            <HeadCell>Descrição</HeadCell>
                            <HeadCell align='right'>Valor</HeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {grupos.map((grupo) => (
                            <React.Fragment key={grupo.classe}>
                                <TableRow className='fechamento-group-row'>
                                    <TableCell colSpan={5} className='fechamento-cell is-bold'>
                                        {grupo.label} <span className='fechamento-muted'>· {grupo.rows.length} lançamentos</span>
                                    </TableCell>
                                    <MoneyCell value={grupo.subtotal} bold />
                                </TableRow>
                                {grupo.categorias.map((categoria) => (
                                    <React.Fragment key={`${grupo.classe}-${categoria.categoria}`}>
                                        <TableRow className='fechamento-subgroup-row'>
                                            <TableCell colSpan={5} className='fechamento-cell'>
                                                {fmtText(categoria.categoria)}{categoria.nome ? ` · ${categoria.nome}` : ''}
                                            </TableCell>
                                            <MoneyCell value={categoria.subtotal} />
                                        </TableRow>
                                        {categoria.rows.map((custo, index) => (
                                            <TableRow key={`${custo.codigo_lancamento}-${index}`} hover>
                                                <TableCell className='fechamento-cell'>
                                                    <Flag tone={custo.tipo === 'RECEBER' ? 'success' : 'neutral'}>{custo.tipo === 'RECEBER' ? 'Receber' : 'Pagar'}</Flag>
                                                </TableCell>
                                                <TableCell className='fechamento-cell'>{fmtText(custo.codigo_lancamento)}</TableCell>
                                                <TableCell className='fechamento-cell'>
                                                    {fmtText(custo.categoria)}
                                                    {custo.nome_categoria && <span className='fechamento-muted'> · {custo.nome_categoria}</span>}
                                                </TableCell>
                                                <TableCell className='fechamento-cell'>{fmtText(custo.fornecedor)}</TableCell>
                                                <TableCell className='fechamento-cell fechamento-descricao-cell'>{fmtText(custo.descricao)}</TableCell>
                                                <MoneyCell value={custo.valor} />
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        ))}
                        {grupos.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align='center' className='fechamento-feedback-cell'>
                                    Nenhum custo para a carteira selecionada.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {grupos.length > 0 && (
                        <TableFooter>
                            <TableRow className='fechamento-total-row'>
                                <TableCell colSpan={5} className='fechamento-cell is-bold'>Total geral</TableCell>
                                <MoneyCell value={totalGeral} bold />
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
        </div>
    );
};

/* ------------------------------------------------------------------ Página */

const ProjetoResumo = ({ label, nome, codigo, resultado }) => {
    const isZerado = !isNil(resultado) && Math.abs(resultado) < ZERADO_LIMITE;

    return (
        <div className={`fechamento-projeto${isZerado ? ' is-zerado' : ''}`}>
            <span className='fechamento-projeto-label'>{label}</span>
            <strong className='fechamento-projeto-nome'>{fmtText(nome)}</strong>
            <span className='fechamento-projeto-codigo'>{fmtText(codigo)}</span>
            <span className='fechamento-projeto-dre'>
                DRE: <strong className={valueClass(resultado)}>{fmtMoney(resultado)}</strong>
                {isZerado && <Flag tone='success'>zerado</Flag>}
            </span>
        </div>
    );
};

const FechamentoConsorcio = () => {
    const [meses, setMeses] = useState([]);
    const [mes, setMes] = useState('');
    const [activeTab, setActiveTab] = useState('total');
    const [total, setTotal] = useState(null);
    const [ugs, setUgs] = useState([]);
    const [custos, setCustos] = useState([]);
    const [isLoadingMeses, setIsLoadingMeses] = useState(true);
    const [isLoadingFechamento, setIsLoadingFechamento] = useState(false);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    const showError = useCallback((texto) => {
        setMessage(texto);
        setStatusAlerta('error');
        setOpenAlerta(true);
    }, []);

    useEffect(() => {
        let ignore = false;

        fetchFechamentoMeses()
            .then((lista) => {
                if (ignore) return;
                setMeses(lista);
                setMes(getDefaultMes(lista));
            })
            .catch((error) => {
                if (!ignore) showError(error.message);
            })
            .finally(() => {
                if (!ignore) setIsLoadingMeses(false);
            });

        return () => { ignore = true; };
    }, [showError]);

    // Total, UGs e Custos vêm juntos a cada troca de mês; UCs carregam sozinhas, paginadas.
    useEffect(() => {
        if (!mes) return undefined;

        let ignore = false;
        setIsLoadingFechamento(true);
        setTotal(null);
        setUgs([]);
        setCustos([]);

        Promise.allSettled([
            fetchFechamentoTotal(mes),
            fetchFechamentoUgs(mes),
            fetchFechamentoCustos(mes)
        ])
            .then(([totalResult, ugsResult, custosResult]) => {
                if (ignore) return;

                if (totalResult.status === 'fulfilled') setTotal(totalResult.value);
                if (ugsResult.status === 'fulfilled') setUgs(ugsResult.value);
                if (custosResult.status === 'fulfilled') setCustos(custosResult.value);

                const falha = [totalResult, ugsResult, custosResult].find((result) => result.status === 'rejected');
                if (falha) showError(falha.reason?.message || 'Falha ao carregar o fechamento.');

                setIsLoadingFechamento(false);
            });

        return () => { ignore = true; };
    }, [mes, showError]);

    const mesSelecionado = meses.find((item) => item.mes === mes);
    const codigoPrincipal = total?.projetos?.principal ?? mesSelecionado?.principal;
    const codigoPpa = total?.projetos?.ppa ?? mesSelecionado?.ppa;
    const dre = total?.dre ?? {};

    const tabCounts = {
        total: total?.alertas?.length || null,
        ugs: ugs.length || null,
        custos: custos.length || null
    };

    return (
        <Box className='mainBox fechamento-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <PageHeader>Fechamento do Consórcio</PageHeader>

            <div className='fechamento-shell'>
                <div className='fechamento-topbar'>
                    <div className='fechamento-topbar-filtros'>
                        <TextField
                            select
                            size='small'
                            label='Mês'
                            value={meses.some((item) => item.mes === mes) ? mes : ''}
                            onChange={(event) => setMes(event.target.value)}
                            disabled={isLoadingMeses || meses.length === 0}
                            sx={{ width: 220 }}
                        >
                            {meses.map((item) => (
                                <MenuItem key={item.mes} value={item.mes}>{item.nome}</MenuItem>
                            ))}
                        </TextField>
                        {(isLoadingMeses || isLoadingFechamento) && <CircularProgress size={20} />}
                    </div>

                    {mesSelecionado && (
                        <div className='fechamento-projetos'>
                            <ProjetoResumo
                                label='Principal'
                                nome={mesSelecionado.nome_principal}
                                codigo={codigoPrincipal}
                                resultado={isNil(codigoPrincipal) ? null : dre[String(codigoPrincipal)]}
                            />
                            <ProjetoResumo
                                label='PPA'
                                nome={mesSelecionado.nome_ppa}
                                codigo={codigoPpa}
                                resultado={isNil(codigoPpa) ? null : dre[String(codigoPpa)]}
                            />
                        </div>
                    )}
                </div>

                {!isLoadingMeses && meses.length === 0 && (
                    <EmptyState>Nenhum mês de fechamento disponível.</EmptyState>
                )}

                {mes && (
                    <>
                        <nav className='fechamento-tabs' aria-label='Seções do fechamento'>
                            {TABS.map((tab) => {
                                const TabIcon = tab.icon;

                                return (
                                    <button
                                        key={tab.key}
                                        type='button'
                                        className={`fechamento-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                        aria-current={activeTab === tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        <TabIcon fontSize='inherit' />
                                        {tab.label}
                                        {!isLoadingFechamento && tabCounts[tab.key] && (
                                            <span className={`fechamento-tab-count${tab.key === 'total' ? ' is-warning' : ''}`}>{tabCounts[tab.key]}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>

                        <Paper className='fechamento-panel'>
                            {activeTab !== 'ucs' && isLoadingFechamento && <LoadingState text='Carregando fechamento...' />}
                            {activeTab === 'total' && !isLoadingFechamento && <TotalTab total={total} mes={mes} />}
                            {activeTab === 'ugs' && !isLoadingFechamento && <UgsTab ugs={ugs} mes={mes} />}
                            {activeTab === 'custos' && !isLoadingFechamento && <CustosTab custos={custos} mes={mes} />}
                            {/* Fica montada para manter filtros e página ao trocar de aba. */}
                            <div hidden={activeTab !== 'ucs'}>
                                <UcsTab mes={mes} isActive={activeTab === 'ucs'} onError={showError} />
                            </div>
                        </Paper>
                    </>
                )}
            </div>

        </Box>
    );
};

export default FechamentoConsorcio;
